import { z } from 'zod';
import { CustomerSapSystemEnum } from './customer.js';
import { InvoiceBlobSchema, InvoiceFormatEnum, InvoiceStatusEnum } from './invoice-blob.js';
import { InvoiceSapSyncSchema, SyncInvoiceToSapResponseSchema } from './invoice-sap.js';

/**
 * Wire-format response for `GET /invoices/:id` (#45).
 *
 * Carries the full `InvoiceBlob` (or `null` for `queued`/`processing`/`error`
 * rows that have no extracted payload yet) plus the metadata the workspace
 * detail view (#45 layout, #47 edit form) renders alongside it. Customer
 * name is denormalised from the join so the route does not have to cascade
 * a second fetch.
 */
export const InvoiceDetailResponseSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  tenantName: z.string(),
  tenantSapSystem: CustomerSapSystemEnum.optional(),
  uploadedById: z.string().nullable(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  sha256: z.string(),
  status: InvoiceStatusEnum,
  format: InvoiceFormatEnum.nullable(),
  errorMessage: z.string().nullable(),
  blob: InvoiceBlobSchema.nullable(),
  sapSync: InvoiceSapSyncSchema.nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});
export type InvoiceDetailResponse = z.infer<typeof InvoiceDetailResponseSchema>;
export { SyncInvoiceToSapResponseSchema };

/**
 * Body of `PATCH /invoices/:id` (#47). MVP: full blob replace; partial-merge
 * lands with #51+ if needed.
 */
export const UpdateInvoiceRequestSchema = z.object({
  blob: InvoiceBlobSchema,
});
export type UpdateInvoiceRequest = z.infer<typeof UpdateInvoiceRequestSchema>;

/**
 * Successful `PATCH /invoices/:id` response. Echo the persisted blob so the
 * client can drop optimistic state in favour of the canonical version
 * (totals re-cast through Zod, etc.).
 */
export const UpdateInvoiceResponseSchema = z.object({
  id: z.string(),
  status: InvoiceStatusEnum,
  blob: InvoiceBlobSchema,
  updatedAt: z.string().datetime(),
});
export type UpdateInvoiceResponse = z.infer<typeof UpdateInvoiceResponseSchema>;

/**
 * Empty body for `POST /invoices/:id/reprocess` (#48). Reserved for future
 * options (`useLatestRules`, ...).
 */
export const ReprocessRequestSchema = z.object({}).strict();
export type ReprocessRequest = z.infer<typeof ReprocessRequestSchema>;

export const ReprocessResponseSchema = z.object({
  invoiceId: z.string(),
  status: InvoiceStatusEnum,
  reprocessedAt: z.string().datetime(),
});
export type ReprocessResponse = z.infer<typeof ReprocessResponseSchema>;

// ---------------------------------------------------------------------------
// PATCH /invoices/:id/status (#49 - manual-review workflow)
// ---------------------------------------------------------------------------

/**
 * Status target a user may transition an invoice to via the manual-review
 * workflow. Excludes pipeline-internal states (`queued`, `processing`) so
 * the API can reject them at the schema layer.
 *
 * Allowed graph (enforced server-side, see route handler):
 *   - manual_review -> success | error
 *   - error         -> manual_review | success
 *   - success       -> manual_review | error
 * Anything else returns a 400 `validation_error` with `code='status.invalid_transition'`.
 */
export const ManualReviewTargetStatusEnum = z.enum(['success', 'manual_review', 'error']);
export type ManualReviewTargetStatus = z.infer<typeof ManualReviewTargetStatusEnum>;

export const UpdateInvoiceStatusRequestSchema = z.object({
  status: ManualReviewTargetStatusEnum,
  /** Optional operator note that lands in the audit `after.note` snapshot. */
  note: z.string().max(500).optional(),
});
export type UpdateInvoiceStatusRequest = z.infer<typeof UpdateInvoiceStatusRequestSchema>;

export const UpdateInvoiceStatusResponseSchema = z.object({
  id: z.string(),
  status: InvoiceStatusEnum,
  updatedAt: z.string().datetime(),
});
export type UpdateInvoiceStatusResponse = z.infer<typeof UpdateInvoiceStatusResponseSchema>;
